"use client";
import React, { useRef, useEffect } from "react";
import { useShouldAnimate } from "@/hooks/useShouldAnimate";

interface NoiseProps {
  patternSize?: number;
  patternScaleX?: number;
  patternScaleY?: number;
  patternRefreshInterval?: number;
  patternAlpha?: number;
  gradientFade?: boolean;
  gradientStart?: number;
  className?: string;
}

const Noise: React.FC<NoiseProps> = ({
  patternSize = 250,
  patternScaleX = 1,
  patternScaleY = 1,
  patternRefreshInterval = 2,
  patternAlpha = 15,
  gradientFade = false,
  gradientStart = 50,
  className,
}) => {
  const grainRef = useRef<HTMLCanvasElement | null>(null);
  const shouldAnimate = useShouldAnimate();

  useEffect(() => {
    if (!shouldAnimate) return;
    const canvas = grainRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return;

    let frame = 0;
    let animationId: number;

    const canvasSize = 1024;

    const resize = () => {
      if (!canvas) return;
      canvas.width = canvasSize;
      canvas.height = canvasSize;

      if (!className) {
        canvas.style.width = "100vw";
        canvas.style.height = "100vh";
      }
    };

    const drawGrain = () => {
      const imageData = ctx.createImageData(canvasSize, canvasSize);
      const data = imageData.data;

      for (let i = 0; i < data.length; i += 4) {
        const value = Math.random() * 255;
        data[i] = value;
        data[i + 1] = value;
        data[i + 2] = value;
        data[i + 3] = patternAlpha;
      }

      ctx.putImageData(imageData, 0, 0);
    };

    const loop = () => {
      if (frame % patternRefreshInterval === 0) {
        drawGrain();
      }
      frame++;
      animationId = window.requestAnimationFrame(loop);
    };

    window.addEventListener("resize", resize);
    resize();
    loop();

    return () => {
      window.removeEventListener("resize", resize);
      window.cancelAnimationFrame(animationId);
    };
  }, [shouldAnimate, patternSize, patternScaleX, patternScaleY, patternRefreshInterval, patternAlpha, className]);

  if (!shouldAnimate) return null;

  return (
    <canvas
      className={className ?? "absolute top-0 left-0 w-screen h-screen pointer-events-none"}
      ref={grainRef}
      style={{
        imageRendering: "pixelated",
        ...(gradientFade && {
          maskImage: `linear-gradient(to bottom, black ${gradientStart}%, transparent 100%)`,
          WebkitMaskImage: `linear-gradient(to bottom, black ${gradientStart}%, transparent 100%)`,
        }),
      }}
    />
  );
};

export default Noise;
